/**
 * Tijd (TIJD) Support Registry
 *
 * Support content (hints, explanations, visuals) for TIJD families.
 *
 * Module: TIJD (Tijd)
 * Families in taxonomy: 16
 * Families with support: 16 (100% coverage)
 *
 * Created: 2026-03-25
 * Status: ✅ Content migrated from familySupportRegistry.ts (Step 4)
 */

import { FamilySupportEntry } from '../familySupportRegistry';

/**
 * TIJD Support Definitions
 *
 * Each entry provides:
 * - defaultExplanation: Student-facing "Zo werkt het" text
 * - hints: Progressive hint steps (title + content)
 * - requiresHints: Whether hints are conceptually needed
 * - requiresVisual: Whether visual aid is conceptually needed
 * - defaultVisual: Optional default visual aid reference
 */
export const TIJD_SUPPORT: Record<string, FamilySupportEntry> = {
  'TIJD-ANALOOG': {
    familyId: 'TIJD-ANALOOG',
    defaultExplanation: 'De kleine wijzer wijst de uren aan, de grote wijzer de minuten. Kwart over = 15 min, half = 30 min, kwart voor = 45 min.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      content: 'Kleine wijzer = uren\nGrote wijzer = minuten\n\n1 uur = 60 minuten'
    }, {
      title: 'Kwartieren',
      content: 'Kwart over = 15 minuten na het hele uur\nHalf = 30 minuten na het hele uur\nKwart voor = 15 minuten voor het volgende uur'
    }]
  },
  'TIJD-DIGITAAL': {
    familyId: 'TIJD-DIGITAAL',
    defaultExplanation: "De digitale klok toont UU:MM (24-uurs notatie). 13:00 = 1 uur 's middags, 00:00 = middernacht. Trek 12 af voor de 'gewone' tijd na 12:00.",
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      content: "De digitale klok toont: UU:MM\nUU = uren (0-23)\nMM = minuten (0-59)\n\n13:00 = 1 uur 's middags\n14:30 = half drie\n00:00 = middernacht"
    }]
  },
  'TIJD-ANALOOG-GEDETAILLEERD': {
    familyId: 'TIJD-ANALOOG-GEDETAILLEERD',
    defaultExplanation: 'Bij het aflezen van de klok met 5-minuten precisie tel je de streepjes op de klok. Elk streepje is 5 minuten.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      content: 'Kleine wijzer = uren\nGrote wijzer = minuten\n\nElk groot streepje = 5 minuten\n1 uur = 60 minuten'
    }, {
      title: 'Minuten tellen',
      content: 'Tel de grote streepjes vanaf de 12:\n1 streepje = 5 min\n2 streepjes = 10 min\n3 streepjes = 15 min (kwart over)\n6 streepjes = 30 min (half)\n9 streepjes = 45 min (kwart voor)'
    }]
  },
  'TIJD-CONVERSIE': {
    familyId: 'TIJD-CONVERSIE',
    defaultExplanation: 'Bij het omzetten van analoog naar digitaal: lees eerst het uur, dan de minuten.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Analoog → Digitaal',
      content: 'Stap 1: Lees het uur (kleine wijzer)\nStap 2: Lees de minuten (grote wijzer)\nStap 3: Schrijf als UU:MM'
    }, {
      title: 'Digitaal → Analoog',
      content: 'Stap 1: Zet kleine wijzer op het uur\nStap 2: Zet grote wijzer op de minuten'
    }]
  },
  'TIJD-DUUR': {
    familyId: 'TIJD-DUUR',
    defaultExplanation: 'Tijdsduur bereken je door het verschil te nemen tussen begin- en eindtijd. Let op: 1 uur = 60 minuten, niet 100!',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Stap 1: Tel naar het volgende hele uur\nStap 2: Tel de hele uren\nStap 3: Tel de resterende minuten\n\nVoorbeeld: 14:45 tot 16:20\n→ 15 min (tot 15:00) + 1 uur (tot 16:00) + 20 min = 1u 35min'
    }, {
      title: 'Let op!',
      content: '1 uur = 60 minuten (niet 100!)\n\nAls je meer dan 60 minuten hebt:\n75 minuten = 1 uur en 15 minuten\n90 minuten = 1 uur en 30 minuten'
    }]
  },
  'TIJD-PLANNING': {
    familyId: 'TIJD-PLANNING',
    defaultExplanation: 'Bij tijdplanning reken je vooruit of achteruit.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Vooruit rekenen',
      content: 'Vertrek + reistijd = aankomst\n\nVoorbeeld: Vertrek 13:40, reis 35 min\n13:40 + 20 min = 14:00\n14:00 + 15 min = 14:15\nAankomst: 14:15'
    }, {
      title: 'Achteruit rekenen',
      content: 'Aankomst - reistijd = vertrek\n\nVoorbeeld: Aankomst 15:00, reis 25 min\n15:00 - 25 min = 14:35\nVertrek: 14:35'
    }]
  },
  'TIJD-SNELHEID': {
    familyId: 'TIJD-SNELHEID',
    defaultExplanation: 'De drie formules: Afstand = snelheid × tijd, Snelheid = afstand ÷ tijd, Tijd = afstand ÷ snelheid.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'De drie formules',
      content: 'Afstand = snelheid × tijd\nSnelheid = afstand : tijd\nTijd = afstand : snelheid\n\nTip: gebruik de AST-driehoek (Afstand boven, Snelheid en Tijd onder).'
    }]
  },
  'TIJD-12-24': {
    familyId: 'TIJD-12-24',
    defaultExplanation: '12-uurs notatie gebruikt AM/PM, 24-uurs notatie telt door tot 24. Na 12:00 tel je 12 op: 1 PM = 13:00.',
    requiresHints: true,
    requiresVisual: true,
    hints: [{
      title: 'Omrekenen',
      content: '12-uurs → 24-uurs (na 12:00):\nTel 12 op: 3 PM = 15:00\n\n24-uurs → 12-uurs (na 12:00):\nTrek 12 af: 15:00 = 3 PM\n\nVoor 12:00 blijft het gelijk:\n9 AM = 9:00'
    }]
  },
  'TIJD-TIJDZONES': {
    familyId: 'TIJD-TIJDZONES',
    defaultExplanation: 'De aarde is verdeeld in 24 tijdzones. Naar het oosten is het later, naar het westen eerder.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Tijdzones',
      content: 'Nederland: UTC+1 (wintertijd) / UTC+2 (zomertijd)\nLonden: 1 uur eerder\nNew York: 6 uur eerder\nTokio: 8 uur later\n\nOost = later, West = eerder'
    }]
  },
  'TIJD-SCHEMA': {
    familyId: 'TIJD-SCHEMA',
    defaultExplanation: "Schema's en roosters lezen: zoek de juiste rij (dag/lijn) en kolom (tijd) om informatie te vinden.",
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Schema lezen',
      content: 'Stap 1: Lees de kolomkoppen (tijden/haltes)\nStap 2: Zoek je vertrekpunt (rij)\nStap 3: Volg de rij naar de juiste kolom\nStap 4: Lees de tijd af'
    }]
  },
  'TIJD-DIAGRAM': {
    familyId: 'TIJD-DIAGRAM',
    defaultExplanation: 'Tijddiagrammen tonen activiteiten over een tijdsperiode. Lees de blokken af om te zien wanneer iets begint en eindigt.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Aflezen',
      content: 'Stap 1: Lees de tijdas (horizontaal)\nStap 2: Lees de activiteiten (verticaal)\nStap 3: Kijk waar blokken beginnen en eindigen\nStap 4: Bereken de duur (eind - begin)'
    }]
  },
  'TIJD-KALENDER': {
    familyId: 'TIJD-KALENDER',
    defaultExplanation: 'Bij kalenderberekeningen tel je dagen, weken of maanden. Let op: maanden hebben niet allemaal evenveel dagen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Dagen per maand',
      content: '31 dagen: jan, mrt, mei, jul, aug, okt, dec\n30 dagen: apr, jun, sep, nov\n28/29 dagen: februari\n\nEzelsbruggetje: knokkels-methode'
    }, {
      title: 'Rekenen met weken',
      content: '1 week = 7 dagen\n2 weken = 14 dagen\n4 weken ≈ 1 maand (maar niet precies!)\n52 weken = 1 jaar'
    }]
  },
  'TIJD-OMREKENEN': {
    familyId: 'TIJD-OMREKENEN',
    defaultExplanation: 'Tijdseenheden omrekenen: 1 uur = 60 minuten, 1 minuut = 60 seconden. Let op: NIET ×100!',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Omrekenen',
      content: '1 uur = 60 minuten\n1 minuut = 60 seconden\n1 uur = 3.600 seconden\n1 dag = 24 uur\n1 week = 7 dagen'
    }, {
      title: 'Let op!',
      content: 'Tijd werkt met 60, niet met 100!\n1,5 uur = 1 uur en 30 minuten (niet 50 min!)\n0,25 uur = 15 minuten\n0,75 uur = 45 minuten'
    }]
  },
  'TIJD-SAMENGESTELD': {
    familyId: 'TIJD-SAMENGESTELD',
    defaultExplanation: 'Bij samengestelde tijdberekeningen combineer je meerdere tijden: eindtijd berekenen, totale duur optellen, pauzes aftrekken.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Stappenplan',
      content: 'Stap 1: Noteer alle begin- en eindtijden\nStap 2: Bereken elke duur apart\nStap 3: Tel de duren op (of trek pauzes af)\nStap 4: Let op: 60 min = 1 uur'
    }]
  },
  'TIJD-AFSTAND': {
    familyId: 'TIJD-AFSTAND',
    defaultExplanation: 'Afstand = snelheid × tijd. Als je 80 km/u rijdt en 2 uur onderweg bent: 80 × 2 = 160 km.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Formule',
      content: 'Afstand = snelheid × tijd\n\nVoorbeeld: 80 km/u × 2 uur = 160 km\n\nLet op de eenheden!\nkm/u × uur = km\nm/s × seconden = m'
    }]
  },
  'TIJD-GEMIDDELDE': {
    familyId: 'TIJD-GEMIDDELDE',
    defaultExplanation: 'Gemiddelde snelheid = totale afstand ÷ totale tijd. Let op: dit is NIET het gemiddelde van twee snelheden!',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Berekenen',
      content: 'Gemiddelde snelheid = totale afstand ÷ totale tijd\n\nVoorbeeld: 120 km in 2 uur\n120 ÷ 2 = 60 km/u gemiddeld\n\nPas op: NIET (80+40)÷2 = 60!'
    }]
  }
};

/**
 * Module Statistics
 * Auto-calculated from support entries
 */
export const TIJD_SUPPORT_STATS = {
  moduleCode: 'TIJD',
  moduleName: 'Tijd',
  totalFamilies: Object.keys(TIJD_SUPPORT).length,
  taxonomyFamilies: 16,
  registryFamilies: 15,
  orphanFamilies: 1,
  // TGL-TIJD-12-24 not in taxonomy
  missingFamilies: 1,
  // 1 taxonomy family missing from registry
  withHints: Object.values(TIJD_SUPPORT).filter(e => e.hints?.length).length,
  withVisuals: Object.values(TIJD_SUPPORT).filter(e => e.defaultVisual).length,
  withExplanations: Object.values(TIJD_SUPPORT).filter(e => e.defaultExplanation).length,
  requiresHintsCount: Object.values(TIJD_SUPPORT).filter(e => e.requiresHints).length,
  requiresVisualCount: Object.values(TIJD_SUPPORT).filter(e => e.requiresVisual).length
};